import apiRequest from '../config/api'

export interface PublicCertificateData {
  certificate: {
    id: number
    user_id: number
    course_id: number
    name: string
    certificate_number?: string | null
    qr_url?: string | null
    evaluation_score?: string | null
    evaluation_passed?: boolean | null
    evaluation_passing_grade?: string | null
    created_at?: string
  }
  course: {
    id: number
    title: string
    type: string
    questions_count?: string | number
    min_passing_score?: string | null
    studied?: string[]
    certificate_materials?: Array<{
      topic?: string | null
      jpl?: string | null
      speaker?: string | null
    }> | null
    start_date?: string | null
    end_date?: string | null
    certificate_event_dates?: string | null
    certificate_jpl_total?: string | null
    certificate_jpl_note?: string | null
    certificate_speakers?: string | null
    certificate_location?: string | null
    certificate_signature_date?: string | null
  } | null
}

export interface PublicCertificateResponse {
  message: string
  data: PublicCertificateData
}

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const extractCertificateNumber = (input: string): string => {
  let value = (input || '').trim()
  if (!value) return ''

  // Handle values that arrive URL-encoded multiple times from scanner apps.
  for (let i = 0; i < 2; i += 1) {
    const decoded = safeDecode(value)
    if (decoded === value) break
    value = decoded.trim()
  }

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value)
      const numberFromQuery = parsed.searchParams.get('number')
      if (numberFromQuery?.trim()) {
        return safeDecode(numberFromQuery.trim())
      }

      const pathParts = parsed.pathname.split('/').filter(Boolean)
      const certificateIndex = pathParts.findIndex(
        (part) => part.toLowerCase() === 'certificate',
      )
      if (certificateIndex >= 0 && pathParts[certificateIndex + 1]) {
        return safeDecode(pathParts[certificateIndex + 1])
      }
    } catch {
      // Fallback to raw value below.
    }
  }

  return value
}

export const getPublicCertificate = async (
  certificateNumber: string,
): Promise<PublicCertificateResponse> => {
  const normalizedNumber = extractCertificateNumber(certificateNumber)
  await apiRequest.getCsrfCookie()
  const encodedNumber = encodeURIComponent(normalizedNumber)
  const res = await apiRequest.get(
    `/api/public/certificates?number=${encodedNumber}`,
  )
  return res.data
}
